import { HierarchyLevel, HierarchyLevelConst, SaturationRow } from '../dtc-commerce-mmm-types';

type CurveField =
  | 'response'
  | 'ciLoResponse'
  | 'ciHiResponse'
  | 'returnNextDollar'
  | 'ciLoReturnNextDollar'
  | 'ciHiReturnNextDollar';

// Linear interpolation of any curve field at `spend`. Clamps to the end points rather than
// extrapolating past what the model actually saw.
export const interpCurve = (curve: SaturationRow[], spend: number, field: CurveField): number => {
  if (curve.length === 0) return 0;
  const first = curve[0];
  const last = curve[curve.length - 1];
  if (spend <= first.adstockSpend) return first[field];
  if (spend >= last.adstockSpend) return last[field];
  for (let i = 0; i < curve.length - 1; i++) {
    const prev = curve[i];
    const next = curve[i + 1];
    if (spend <= next.adstockSpend) {
      const span = next.adstockSpend - prev.adstockSpend;
      if (span <= 0) return prev[field];
      const frac = (spend - prev.adstockSpend) / span;
      return prev[field] + frac * (next[field] - prev[field]);
    }
  }
  return last[field];
};

// A curve without upper/lower next-dollar estimates has no plausible range to read, so it's
// never treated as certain.
export const hasConfidenceBands = (curve: SaturationRow[]): boolean =>
  curve.some(p => p.ciHiReturnNextDollar > 0);

export interface ReturnRange {
  expected: number;
  low: number;
  high: number;
}

// Next-dollar return at `spend`, with its pessimistic and optimistic ends.
export const returnRangeAt = (curve: SaturationRow[], spend: number): ReturnRange => ({
  expected: interpCurve(curve, spend, 'returnNextDollar'),
  low: interpCurve(curve, spend, 'ciLoReturnNextDollar'),
  high: interpCurve(curve, spend, 'ciHiReturnNextDollar')
});

interface FeatureIdentity {
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brandNonbrand: string;
}

// Display name matches V1–V3: comma-joined channel/platform/tactic/brand_nonbrand.
export const buildDisplayName = (
  row: FeatureIdentity,
  formatPart: (part: string) => string
): string => {
  const parts = [row.channel, row.platform, row.tactic, row.brandNonbrand]
    .filter(Boolean)
    .map(formatPart);
  return parts.join(', ') || row.name;
};

// The drawer's group key for a feature at the page's current hierarchy level.
export const buildGroupKey = (row: FeatureIdentity, level: HierarchyLevel): string => {
  switch (level) {
    case HierarchyLevelConst.PLATFORM:
      return `${row.channel}|${row.platform}`;
    case HierarchyLevelConst.TACTIC:
      return `${row.channel}|${row.platform}|${row.tactic}`;
    case HierarchyLevelConst.BRAND_NONBRAND:
      return row.name;
    case HierarchyLevelConst.CHANNEL:
    default:
      return row.channel;
  }
};

// Compact amounts that keep one decimal in the low thousands so small shifts don't all read
// as "$1K": 1_132_000 → "1.13M", 16_166 → "16K", 1_244 → "1.2K", 412 → "412".
const formatCompactPrecise = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${Math.round(abs).toLocaleString('en-US')}`;
};

export const formatDollarsPrecise = (value: number): string => {
  const formatted = formatCompactPrecise(value);
  return formatted.startsWith('-') ? `-$${formatted.slice(1)}` : `$${formatted}`;
};
